/**
 * ResumeConflictDialog — 3-option dialog shown when the doctor clicks
 * "Resume Visit" while they already have ANOTHER visit IN_PROGRESS.
 *
 * Options:
 *   - Park to queue — current visit IN_PROGRESS → QUEUE (priority
 *     requeue, smart slot), then open the resumed one.
 *   - Finish [current] first — close dialog, stay on current visit.
 *   - Cancel — close dialog, no state change.
 *
 * Patient-name oriented copy: the title names the current patient, the
 * body names the one being resumed. Familiar phrasing for busy clinics.
 *
 * Styling mirrors ConfirmDialog (WorkflowShared.jsx) and obeys both:
 *   - Day-mode contrast rule (solid backgrounds, no faint tints).
 *   - Button-visibility rule (every button has explicit bg in both modes).
 */
import React from 'react';
import { useTheme } from '../../theme/ThemeContext';

const patientNameOf = (visit) => {
  if (!visit) return 'the patient';
  return (
    visit.patient_name ||
    `${visit.first_name || ''} ${visit.last_name || ''}`.trim() ||
    'the patient'
  );
};

export default function ResumeConflictDialog({
  isDark: isDarkProp,
  open,
  currentVisit,
  targetVisit,
  inFlight = false,
  error = null,
  onPark,
  onFinishCurrent,
  onCancel,
}) {
  // Read theme from context as the default; allow explicit prop override
  // for hosts that already pass it through.
  const theme = useTheme();
  const isDark = isDarkProp !== undefined ? isDarkProp : !!theme?.isDark;

  if (!open) return null;

  const currentName = patientNameOf(currentVisit);
  const targetName = patientNameOf(targetVisit);

  const bg = isDark ? '#1f2937' : '#ffffff';
  const textPrimary = isDark ? '#f5f5f7' : '#111827';
  const textSecondary = isDark ? '#d4d4d8' : '#374151';
  const textMuted = isDark ? '#9ca3af' : '#6b7280';

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget && !inFlight) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          background: bg,
          borderRadius: '1rem',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.25rem 0.75rem',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '50%',
              background: isDark ? 'rgba(20,184,166,0.20)' : 'rgba(13,148,136,0.12)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '0.625rem',
            }}
          >
            <span style={{ fontSize: '1.125rem' }}>↻</span>
          </div>
          <h3
            style={{
              margin: 0,
              fontSize: '0.9375rem',
              fontWeight: 700,
              color: textPrimary,
            }}
          >
            You&apos;re currently with {currentName}
          </h3>
          <p
            style={{
              margin: '0.5rem 0 0',
              fontSize: '0.8125rem',
              color: textSecondary,
              lineHeight: 1.5,
            }}
          >
            {targetName}&apos;s visit needs you next. What would you like to do?
          </p>
          {error && (
            <p
              style={{
                margin: '0.625rem 0 0',
                fontSize: '0.75rem',
                color: '#b91c1c',
                background: isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)',
                padding: '0.375rem 0.625rem',
                borderRadius: '0.375rem',
                border: `1px solid ${isDark ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.25)'}`,
              }}
            >
              {error}
            </p>
          )}
        </div>

        {/* Actions — stacked vertically since labels carry patient names */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem 1.25rem',
          }}
        >
          {/* Primary: Park current to queue */}
          <button
            type="button"
            onClick={onPark}
            disabled={inFlight}
            style={{
              cursor: inFlight ? 'wait' : 'pointer',
              padding: '0.625rem 1rem',
              borderRadius: '0.625rem',
              border: 'none',
              background: '#0d9488',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: '#ffffff',
              opacity: inFlight ? 0.6 : 1,
              transition: 'background 0.15s, opacity 0.15s',
            }}
          >
            {inFlight
              ? 'Resuming…'
              : `Park ${currentName} & open ${targetName}`}
          </button>

          {/* Secondary: Finish current first */}
          <button
            type="button"
            onClick={onFinishCurrent}
            disabled={inFlight}
            style={{
              cursor: inFlight ? 'not-allowed' : 'pointer',
              padding: '0.625rem 1rem',
              borderRadius: '0.625rem',
              border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
              background: isDark ? '#334155' : '#ffffff',
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: textPrimary,
              opacity: inFlight ? 0.6 : 1,
              transition: 'background 0.15s, opacity 0.15s',
            }}
          >
            Finish {currentName} first
          </button>

          {/* Tertiary: Cancel */}
          <button
            type="button"
            onClick={onCancel}
            disabled={inFlight}
            style={{
              cursor: inFlight ? 'not-allowed' : 'pointer',
              padding: '0.5rem 1rem',
              borderRadius: '0.625rem',
              border: 'none',
              background: 'transparent',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: textMuted,
              opacity: inFlight ? 0.6 : 1,
              transition: 'color 0.15s, opacity 0.15s',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
